import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const STATUS_LABELS = {
  pending: "In attesa",
  drafted: "Bozza",
  replied: "Risposte",
  skipped: "Saltate",
  failed: "Errori",
};
const STATUS_COLORS = {
  pending: "#B5A98C",
  drafted: "#8C8B8A",
  replied: "#2C4C3B",
  skipped: "#D7C9A5",
  failed: "#C85A32",
};

const LANG_LABELS = {
  it: "Italiano",
  en: "Inglese",
  es: "Spagnolo",
  fr: "Francese",
  de: "Tedesco",
  pt: "Portoghese",
  nl: "Olandese",
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [rangeDays, setRangeDays] = useState(14);

  useEffect(() => {
    api.get("/reports?limit=30").then(({ data }) => setReports(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/reports/analytics?days=${rangeDays}`).then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [rangeDays]);

  return (
    <div className="space-y-10">
      <div>
        <div className="label-mono">Storico & Analytics</div>
        <h1 className="font-display text-4xl mt-2 font-light">Resoconti e Statistiche</h1>
        <p className="text-sm text-[#5C5B5A] mt-3 max-w-xl">
          La Segretaria produce un resoconto operativo automatico ogni 4 ore. Qui trovi grafici dell&apos;attività e lo storico completo.
        </p>
      </div>

      {/* ANALYTICS SECTION */}
      <section className="space-y-6" data-testid="analytics-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-2xl font-light">Andamento attività</h2>
          <div className="flex gap-1" data-testid="analytics-range-selector">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`analytics-range-${d}`}
                onClick={() => setRangeDays(d)}
                className={`px-3 py-2 text-[10px] font-mono uppercase tracking-[0.15em] border transition-colors ${
                  rangeDays === d
                    ? "border-[#2C4C3B] bg-[#2C4C3B] text-white"
                    : "border-[#E5E4E0] bg-white text-[#5C5B5A] hover:bg-[#F0EFEA]"
                }`}
              >
                {d} giorni
              </button>
            ))}
          </div>
        </div>

        {!analytics ? (
          <div className="border border-dashed border-[#E5E4E0] p-10 text-center text-sm text-[#8C8B8A]">
            Caricamento analytics…
          </div>
        ) : analytics.total_emails === 0 ? (
          <div className="border border-dashed border-[#E5E4E0] p-10 text-center text-sm text-[#8C8B8A]" data-testid="analytics-empty">
            Nessuna email negli ultimi {rangeDays} giorni. I grafici appariranno quando inizierai a ricevere richieste.
          </div>
        ) : (
          <>
            {/* DAILY VOLUME */}
            <div className="border border-[#E5E4E0] bg-white p-6" data-testid="chart-daily">
              <div className="label-mono mb-4">Volume giornaliero · per stato</div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={analytics.daily} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grRepl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2C4C3B" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#2C4C3B" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="grSkip" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D7C9A5" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#D7C9A5" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="grFail" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C85A32" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#C85A32" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E5E4E0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => d.slice(5)}
                    tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#8C8B8A" }}
                    stroke="#E5E4E0"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#8C8B8A" }}
                    stroke="#E5E4E0"
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#1C1B1A",
                      border: "none",
                      borderRadius: 0,
                      fontFamily: "JetBrains Mono",
                      fontSize: 11,
                      color: "#F9F8F6",
                    }}
                    labelStyle={{ color: "#D7C9A5" }}
                  />
                  <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10 }} />
                  <Area type="monotone" dataKey="replied" name="Risposte" stroke="#2C4C3B" fill="url(#grRepl)" strokeWidth={2} />
                  <Area type="monotone" dataKey="skipped" name="Saltate" stroke="#D7C9A5" fill="url(#grSkip)" strokeWidth={2} />
                  <Area type="monotone" dataKey="failed" name="Errori" stroke="#C85A32" fill="url(#grFail)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* STATUS PIE */}
              <div className="border border-[#E5E4E0] bg-white p-6" data-testid="chart-status">
                <div className="label-mono mb-4">Distribuzione per stato</div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={analytics.status_distribution}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={85}
                      paddingAngle={2}
                      label={(entry) => STATUS_LABELS[entry.status] || entry.status}
                      labelLine={false}
                      style={{ fontFamily: "JetBrains Mono", fontSize: 10 }}
                    >
                      {analytics.status_distribution.map((entry) => (
                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || "#8C8B8A"} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, _name, props) => [`${value} email`, STATUS_LABELS[props.payload.status]]}
                      contentStyle={{
                        background: "#1C1B1A",
                        border: "none",
                        borderRadius: 0,
                        fontFamily: "JetBrains Mono",
                        fontSize: 11,
                        color: "#F9F8F6",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* TOP SENDERS */}
              <div className="border border-[#E5E4E0] bg-white p-6" data-testid="chart-senders">
                <div className="label-mono mb-4">Top 5 mittenti</div>
                {analytics.top_senders.length === 0 ? (
                  <div className="text-sm text-[#8C8B8A] py-10 text-center">Nessun mittente</div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={analytics.top_senders}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="2 4" stroke="#E5E4E0" horizontal={false} />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#8C8B8A" }}
                        stroke="#E5E4E0"
                      />
                      <YAxis
                        type="category"
                        dataKey="address"
                        width={170}
                        tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#5C5B5A" }}
                        tickFormatter={(v) => (v.length > 22 ? v.slice(0, 22) + "…" : v)}
                        stroke="#E5E4E0"
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#1C1B1A",
                          border: "none",
                          borderRadius: 0,
                          fontFamily: "JetBrains Mono",
                          fontSize: 11,
                          color: "#F9F8F6",
                        }}
                      />
                      <Bar dataKey="count" fill="#2C4C3B" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* LANGUAGE DISTRIBUTION */}
            {analytics.language_distribution.length > 1 && (
              <div className="border border-[#E5E4E0] bg-white p-6" data-testid="chart-language">
                <div className="label-mono mb-4">Lingue ricevute</div>
                <div className="flex flex-wrap gap-3">
                  {analytics.language_distribution.map((l) => (
                    <div
                      key={l.language}
                      className="flex items-baseline gap-2 px-4 py-3 border border-[#E5E4E0] bg-[#F9F8F6]"
                      data-testid={`lang-pill-${l.language}`}
                    >
                      <span className="label-mono">{LANG_LABELS[l.language] || l.language}</span>
                      <span className="font-display text-2xl font-light text-[#2C4C3B]">{l.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* REPORTS HISTORY */}
      <section className="space-y-4">
        <h2 className="font-display text-2xl font-light">Storico resoconti</h2>
        {reports.length === 0 ? (
          <div className="border border-dashed border-[#E5E4E0] p-12 text-center text-sm text-[#8C8B8A]">
            Nessun resoconto ancora. Verrà generato automaticamente.
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((r) => (
              <article key={r.id} data-testid={`report-${r.id}`} className="border border-[#E5E4E0] bg-white p-6 fade-up">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <div className="label-mono">Finestra</div>
                    <div className="text-sm font-mono mt-1 text-[#1C1B1A]">
                      {new Date(r.window_start).toLocaleString("it-IT")} <span className="text-[#8C8B8A]">→</span>{" "}
                      {new Date(r.window_end).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div className="flex gap-6 text-center">
                    <div>
                      <div className="label-mono text-[10px]">Ricevute</div>
                      <div className="font-display text-3xl font-light">{r.emails_received}</div>
                    </div>
                    <div>
                      <div className="label-mono text-[10px]">Risposte</div>
                      <div className="font-display text-3xl font-light text-[#2C4C3B]">{r.emails_replied}</div>
                    </div>
                    <div>
                      <div className="label-mono text-[10px]">Errori</div>
                      <div className="font-display text-3xl font-light text-[#C85A32]">{r.emails_failed}</div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#1C1B1A] mt-5 leading-relaxed italic border-t border-[#E5E4E0] pt-4">
                  {r.summary_text}
                </p>
                {r.top_senders?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.top_senders.map((s) => (
                      <span key={s.address} className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 bg-[#F0EFEA] text-[#5C5B5A]">
                        {s.address} · {s.count}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
